import { useCallback, useEffect, useState } from 'react';

// DEV-10764: light/dark theme for the public site, shared with the web app.
//
// Resolution + persistence mirror the locale plumbing in LanguagePicker:
//   - A `theme` cookie at the apex domain (.lunarcrush.com) so the choice
//     carries to app.lunarcrush.com / onboarding on the next request. The
//     value (`dark` | `light`) is exactly what apps/app's next-themes uses,
//     so its pre-hydration shim can adopt it verbatim.
//   - localStorage as the same-origin fallback.
//
// The actual <html data-theme> attribute is set BEFORE first paint by the
// inline script in index.html (no FOUC); this hook reads that as the source
// of truth on mount and is the only writer afterwards.

export type ColorScheme = 'dark' | 'light';

export const THEME_COOKIE = 'theme';
export const THEME_STORAGE_KEY = 'lc.theme';
const DEFAULT_SCHEME: ColorScheme = 'dark';

const isScheme = (v: unknown): v is ColorScheme => v === 'dark' || v === 'light';

function readCookie(): ColorScheme | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)theme=([^;]+)/);
  return isScheme(m?.[1]) ? (m![1] as ColorScheme) : null;
}

/** Resolve the active scheme: live attribute → cookie → storage → default (dark).
 *  The OS `prefers-color-scheme` is intentionally not used — dark is the product
 *  default until the user explicitly chooses (matches the ticket + the pre-hydration
 *  script in index.html). */
export function resolveScheme(): ColorScheme {
  if (typeof document === 'undefined') return DEFAULT_SCHEME;
  const attr = document.documentElement.dataset.theme;
  if (isScheme(attr)) return attr;
  const fromCookie = readCookie();
  if (fromCookie) return fromCookie;
  try {
    const fromStorage = localStorage.getItem(THEME_STORAGE_KEY);
    if (isScheme(fromStorage)) return fromStorage;
  } catch {
    /* storage may be unavailable (private mode) */
  }
  return DEFAULT_SCHEME;
}

/** Persist + apply a scheme across the document, cookie, and storage. */
export function applyScheme(scheme: ColorScheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = scheme;

  const host = window.location.hostname;
  const isApex = host.endsWith('lunarcrush.com');
  const domainAttr = isApex ? '; domain=.lunarcrush.com' : '';
  document.cookie = `${THEME_COOKIE}=${scheme}; path=/; max-age=31536000; samesite=lax${domainAttr}`;

  try {
    localStorage.setItem(THEME_STORAGE_KEY, scheme);
  } catch {
    /* ignore */
  }
}

/**
 * Public-site theme controller. Returns the active scheme plus setters that
 * persist to the apex cookie so the web app inherits the choice.
 */
export function useColorScheme() {
  const [scheme, setScheme] = useState<ColorScheme>(DEFAULT_SCHEME);

  // Adopt whatever the pre-hydration script resolved (avoids a flash).
  useEffect(() => {
    setScheme(resolveScheme());
  }, []);

  const setColorScheme = useCallback((next: ColorScheme) => {
    applyScheme(next);
    setScheme(next);
  }, []);

  const toggle = useCallback(() => {
    setColorScheme(scheme === 'dark' ? 'light' : 'dark');
  }, [scheme, setColorScheme]);

  return { scheme, setColorScheme, toggle } as const;
}
